import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { HomepageSettingsService } from '../firebase';

const HeroContainer = styled.section`
  height: 100vh;
  background: #fff;
  position: relative;
  overflow: hidden;
`;

const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const Slide = styled.div<{ $isActive: boolean; $bgImage: string; $isClickable?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url(${props => props.$bgImage}) center/cover;
  opacity: ${props => props.$isActive ? 1 : 0};
  transition: opacity 1s ease-in-out;
  cursor: ${props => props.$isClickable ? 'pointer' : 'default'};
`;

const DotsContainer = styled.div`
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  z-index: 10;
`;

const Dot = styled.button<{ $isActive: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$isActive ? '#1a1a1a' : 'rgba(255, 255, 255, 0.5)'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #1a1a1a;
  }
`;

const LoadingContainer = styled.div`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  color: #666;
`;

const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Varsayılan resimler (Firebase'de veri yoksa kullanılacak)
  const defaultSlides = [
    '/hero-section/hero-section1.webp',
    '/hero-section/hero-section-deneme.webp',
    '/hero-section/herosection3.webp',
    '/hero-section/herosection4.webp',
    '/hero-section/herosection5.webp',
    '/hero-section/herosection6.webp'
  ];

  useEffect(() => {
    loadHeroSlides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHeroSlides = async () => {
    setLoading(true);
    try {
      const result = await HomepageSettingsService.getHomepageSettings();
      if (result.success && result.data?.heroSlides && result.data.heroSlides.length > 0) {
        setSlides(result.data.heroSlides);
      } else {
        // Firebase'de veri yoksa varsayılan resimleri kullan
        setSlides(defaultSlides);
      }
    } catch (error) {
      console.error('Error loading hero slides:', error);
      setSlides(defaultSlides);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (slides.length === 0) return;
    
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);

    return () => clearInterval(timer);
  }, [slides.length]);

  if (loading) {
    return (
      <LoadingContainer>
        <div>Yükleniyor...</div>
      </LoadingContainer>
    );
  }

  if (slides.length === 0) {
    return (
      <HeroContainer>
        <LoadingContainer>
          <div>Henüz resim eklenmedi</div>
        </LoadingContainer>
      </HeroContainer>
    );
  }

  const handleSlideClick = (slideUrl: string) => {
    // herosection6'ya tıklandığında randevu sayfasına yönlendir
    if (slideUrl.includes('herosection6.webp')) {
      navigate('/randevu');
    }
  };

  return (
    <HeroContainer>
      <SliderContainer>
        {slides.map((slide, index) => (
          <Slide 
            key={index} 
            $bgImage={slide} 
            $isActive={index === currentSlide}
            $isClickable={slide.includes('herosection6.webp')}
            onClick={() => handleSlideClick(slide)}
          />
        ))}
        
        {slides.length > 1 && (
          <DotsContainer>
            {slides.map((_, index) => (
              <Dot
                key={index}
                $isActive={index === currentSlide}
                onClick={() => setCurrentSlide(index)}
              />
            ))}
          </DotsContainer>
        )}
      </SliderContainer>
    </HeroContainer>
  );
};

export default HeroSection;